import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Typography,
  Link as MuiLink,
  CardActions,
  Button,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CategoryIcon from '@mui/icons-material/Category';
import ListPageLayout from '../components/shared/list-page-layout';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { IListUsefulLinksRequest } from '../../model/base/request/list-useful-links-request';
import { useUsefulLinksController } from './context/useful-links-controller';
import { useUsefulLinksState } from './context/useful-links-context';

export function UsefulLinksComponent() {
  const { loadLinks, loadCategories } = useUsefulLinksController();
  const { links, categories, loading } = useUsefulLinksState();
  const [filters, setFilters] = useState<IListUsefulLinksRequest>({
    page: 0,
    pageSize: 100,
    term: '',
    categoryId: undefined,
  });

  useEffect(() => {
    loadCategories();
    loadLinks(filters);
  }, []);

  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.description
  }));

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por nome ou descrição',
      fullWidth: false,
      gridSize: { xs: 12, sm: 12, md: 8 },
    },
    {
      name: 'categoryId',
      label: 'Categoria',
      type: 'select',
      options: categoryOptions,
      gridSize: { xs: 12, sm: 12, md: 4 },
    },
  ];

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    loadLinks(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters: IListUsefulLinksRequest = {
      page: 0,
      pageSize: 100,
      term: '',
      categoryId: undefined,
    };
    setFilters(clearedFilters);
    loadLinks(clearedFilters);
  };

  return (
    <ListPageLayout
      title="Links Úteis"
      subtitle="Acesse links importantes e recursos externos"
      breadcrumbs={[
        { label: 'Links Úteis' },
      ]}
      filters={
        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof IListUsefulLinksRequest],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={loading}
        />
      }
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      ) : links.length === 0 ? (
        <Alert severity="info">
          {filters.term || filters.categoryId
            ? 'Nenhum link útil encontrado com os filtros aplicados.'
            : 'Nenhum link útil cadastrado no momento.'}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {links.map((link) => (
            <Grid item xs={12} sm={6} md={4} key={link.id}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Ícone e Nome */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                      pb: 2,
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        color: 'white',
                      }}
                    >
                      <LinkIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        flex: 1,
                        wordBreak: 'break-word',
                      }}
                    >
                      {link.name}
                    </Typography>
                  </Box>

                  {/* Categoria */}
                  {link.category && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <CategoryIcon color="action" fontSize="small" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        {link.category}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    component={MuiLink}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    fullWidth
                    endIcon={<OpenInNewIcon />}
                    sx={{
                      textTransform: 'none',
                    }}
                  >
                    Acessar Link
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </ListPageLayout>
  );
}
